/** Доступно любому вошедшему пользователю. */
export const ANY = 'ANY' as const
export type RouteAccess = `${string}:${string}` | typeof ANY

/**
 * Единая карта доступа frontend-маршрутов. Ключи с `/*` — явные поддеревья;
 * обычная запись покрывает только точный путь, поэтому новый экран не получает
 * доступ случайно через родительский URL.
 */
export const ROUTE_ACCESS: Record<string, RouteAccess> = {
  '/': 'Product:READ',
  '/themes-board': 'ThemesBoard:READ',
  '/ideas': 'Ideas:READ',
  '/pipeline': 'Pipeline:READ',
  '/pipeline/source-quality': 'Pipeline:READ',
  '/pipeline-rules': 'Product:READ',
  '/attributes': 'Product:READ',
  '/brands': 'Product:READ',
  '/categories': 'Product:READ',
  '/certificates': 'Product:READ',
  '/design-type-matrix': 'Product:READ',
  '/marketplace-mapping': 'Product:READ',
  '/marketplace/publish/*': 'Card:UPDATE',
  '/marketplace/card-changes': 'Card:READ',
  '/media-mappings': 'Product:READ',
  '/article-mappings': 'Product:READ',
  '/upload-templates': 'Product:READ',
  '/content-funnels': 'ContentFunnel:READ',
  '/content-funnel-studio': 'ContentFunnel:READ',
  '/content-funnel-studio/*': 'ContentFunnel:READ',
  // Отдельный путь (не поддерево `/content-funnel-studio/*`, дефис вместо
  // слэша): резерв студии v2 на время, пока сервер не готов создавать v2-
  // проекты (см. router.tsx, StudioNewProject).
  '/content-funnel-studio-legacy': 'ContentFunnel:READ',
  // Воронка в стиле серии: список и поддерево (`new`, `:runId`). Запись на сервере — ContentFunnel:UPDATE.
  '/content-funnel-series': 'ContentFunnel:READ',
  '/content-funnel-series/*': 'ContentFunnel:READ',
  '/generation-templates': 'ContentFunnel:READ',
  '/search': 'Product:READ',
  '/products': 'Product:READ',
  '/products-old': 'Product:READ',
  '/products-v2': 'Product:READ',
  '/products/bulk-edit': 'Product:READ',
  '/gingerbreads': 'Gingerbread:READ',
  '/gingerbread-generator': 'Gingerbread:READ',
  '/gingerbread-generator/new': 'Gingerbread:CREATE',
  '/gingerbread-generator/studio/*': 'Gingerbread:READ',
  '/gingerbread-generator/shapes': 'Product:READ',
  '/gingerbread-generator/legacy': 'Gingerbread:READ',
  '/gingerbread-generator/jobs/*': 'Gingerbread:READ',
  '/layout-generator': 'LayoutGenerator:READ',
  '/layout-generator/new': 'LayoutGenerator:CREATE',
  '/layout-generator/studio/*': 'LayoutGenerator:CREATE',
  '/layout-generator/duplicate': 'LayoutGenerator:CREATE',
  '/layout-generator/duplicate/*': 'LayoutGenerator:CREATE',
  '/layout-generator/help': 'LayoutGenerator:READ',
  '/asset-library': 'Pipeline:READ',
  '/layout-generator/*': 'LayoutGenerator:READ',
  '/layout-generator-v2': 'LayoutGenerator:READ',
  '/layout-generator-v2/new': 'LayoutGenerator:CREATE',
  '/layout-generator-v2/studio/*': 'LayoutGenerator:CREATE',
  '/layout-generator-v2/duplicate': 'LayoutGenerator:CREATE',
  '/layout-generator-v2/duplicate/*': 'LayoutGenerator:CREATE',
  '/layout-generator-v2/*': 'LayoutGenerator:READ',
  '/designs': 'Product:READ',
  '/designs-old': 'Product:READ',
  '/designs-v2': 'Product:READ',
  // Бывшая заглушка — переадресация на «Макеты и темы» (волна 4), доступ как у страницы назначения.
  '/designs-analytics': 'SalesAnalytics:READ',
  '/wildberries': 'Marketplace:READ',
  '/wildberries-old': 'Marketplace:READ',
  '/wildberries-v2': 'Marketplace:READ',
  '/wildberries/*': 'Marketplace:READ',
  '/ozon': 'Marketplace:READ',
  '/ozon-old': 'Marketplace:READ',
  '/ozon-v2': 'Marketplace:READ',
  '/ozon/*': 'Marketplace:READ',
  '/yandex-market': 'Marketplace:READ',
  '/yandex-market/*': 'Marketplace:READ',
  '/onec-products': 'OneC:READ',
  '/site-cards': 'Site:READ',
  '/site-products': 'Site:READ',
  '/products/wb-cards': 'Marketplace:READ',
  // Планирование поставок FBO (волна 4): просмотр — SalesAnalytics:READ; расчёт, правка, утверждение и
  // настройки — кнопки только при SalesAnalytics:UPDATE (сервер проверяет то же право).
  '/supplies/wb': 'SalesAnalytics:READ',
  '/supplies/ozon': 'SalesAnalytics:READ',
  '/orders': 'Marketplace:READ',
  '/products-seo': 'Seo:READ',
  '/products-seo/*': 'Seo:READ',
  '/products-seo-v2': 'Seo:READ',
  '/products-seo-v2/*': 'Seo:READ',
  // Волна 5 «Деньги»: юнит-экономика и ценообразование Ozon — страницы аналитики (SalesAnalytics:READ), явная
  // запись важнее поддерева '/pricing/*'. Страница ценообразования WB остаётся под Pricing:READ.
  '/pricing/unit-economics': 'SalesAnalytics:READ',
  '/pricing/ozon': 'SalesAnalytics:READ',
  '/pricing/*': 'Pricing:READ',
  '/sellers': 'Seller:READ',
  '/designers': 'Finance:READ',
  '/designer-payouts': 'Finance:READ',
  '/designer/create': 'DesignerCabinet:READ',
  '/designer/stats': 'DesignerCabinet:READ',
  '/designer/payouts': 'DesignerCabinet:READ',
  '/designer/tariffs': 'DesignerCabinet:READ',
  // Бывшие заглушки — переадресация на «Сезонный календарь» (волна 4).
  '/seasonal/products': 'SalesAnalytics:READ',
  '/seasonal/events': 'SalesAnalytics:READ',
  '/analytics/heroes': 'Analytics:READ',
  // Аналитика продаж (волна 2): просмотр — SalesAnalytics:READ; «Догрузить период» на «Качестве данных» —
  // только при SalesAnalytics:UPDATE. Ресурс Analytics остаётся только у «Товаров героев».
  '/analytics/sales': 'SalesAnalytics:READ',
  '/analytics/data-quality': 'SalesAnalytics:READ',
  // Справочник метрик MCP «Бизнес» (только чтение; бэкенд — GET /analytics/metrics/registry, тот же ресурс SalesAnalytics:READ).
  '/analytics/metrics': 'SalesAnalytics:READ',
  // Исследователь метрик: любая метрика MCP «Бизнес» (бэкенд — /business-web/*). Страница открыта разделу аналитики продаж, а
  // доступ к ДАННЫМ проверяет гейтвей: роль BusinessMcp:READ и кабинеты pim_business_access_sellers, финансовые и субъектные
  // метрики — правами Business*:READ. Роли «Бизнес» выдаются отдельной таблицей pim_business_user_roles и в список прав сессии
  // фронта не попадают, поэтому проверять их здесь нельзя: без них страница показывает ответ гейтвея «Нет доступа к данным».
  '/analytics/explorer': 'SalesAnalytics:READ',
  // Ассортимент (волна 3): сезонный календарь, макеты и темы, ABC/XYZ, кабинеты — тот же ресурс SalesAnalytics:READ.
  '/analytics/seasonal': 'SalesAnalytics:READ',
  '/analytics/designs': 'SalesAnalytics:READ',
  '/analytics/abc': 'SalesAnalytics:READ',
  '/analytics/cabinets': 'SalesAnalytics:READ',
  // Остатки и оборачиваемость (волна 4).
  '/analytics/stocks': 'SalesAnalytics:READ',
  // «Деньги» (волна 5): P&L, расходы площадок (правка классификатора начислений — кнопки только при
  // SalesAnalytics:UPDATE), сверка и выплаты. Ресурс Finance не используется: он выдан роли analytic под выплаты дизайнерам.
  '/analytics/pnl': 'SalesAnalytics:READ',
  '/analytics/expenses': 'SalesAnalytics:READ',
  '/analytics/reconciliation': 'SalesAnalytics:READ',
  // «Управление» (волна 6): сводка, алерты, РНП, план-факт и страницы разбора — воронка, реклама, цены и акции,
  // отзывы, география. Просмотр — SalesAnalytics:READ; запись (статус и ответственный алерта, правила и сводка в
  // Битрикс24, гипотезы РНП, ввод плана) — кнопки только при SalesAnalytics:UPDATE. `/analytics` — точный путь:
  // поддерева `/analytics/*` нет, у каждой страницы раздела своя явная запись.
  '/analytics': 'SalesAnalytics:READ',
  '/analytics/alerts': 'SalesAnalytics:READ',
  '/analytics/rnp': 'SalesAnalytics:READ',
  '/analytics/plan': 'SalesAnalytics:READ',
  '/analytics/funnel': 'SalesAnalytics:READ',
  '/analytics/ads': 'SalesAnalytics:READ',
  '/analytics/prices': 'SalesAnalytics:READ',
  '/analytics/feedbacks': 'SalesAnalytics:READ',
  '/analytics/geo': 'SalesAnalytics:READ',
  // Возвраты и вывоз (фаза 2 аналитики): страница читает слой MCP «Бизнес» (/business-web/*). Как у Исследователя, она открыта разделу
  // аналитики продаж, а доступ к ДАННЫМ проверяет гейтвей (роль BusinessMcp:READ и кабинеты); роли «Бизнес» в правах сессии фронта нет.
  '/analytics/returns': 'SalesAnalytics:READ',
  // Себестоимость: просмотр — CostPrice:READ; кнопки правки на странице — только при CostPrice:UPDATE.
  '/analytics/cost': 'CostPrice:READ',
  '/users': 'System:READ',
  '/settings/access': 'System:READ',
  '/integrations': 'System:READ',
  '/integrations/*': 'System:READ',
  // Вкладка Licensium (037): своё право, а не System — её видят и те, кому
  // выданы только лицензии; явная запись важнее поддерева `/integrations/*`.
  '/integrations/licensium': 'Licensium:READ',
  // Привязка сотрудника Битрикс24 к СВОЕМУ аккаунту ПИМ по одноразовой ссылке бота: нужен только вход (явная запись
  // важнее поддерева `/integrations/*`, иначе страница потребовала бы System:READ). Права MCP «Бизнес» бот проверяет сам.
  '/integrations/bitrix/link': ANY,
  '/moysklad': 'System:READ',
  '/admin/console': 'System:READ',
  '/admin/logs': 'System:READ',
  '/audit-log': 'System:READ',
  '/settings/storage': 'System:READ',
  '/settings/ftp': 'System:READ',
  '/settings/makets-storage': 'System:READ',
  '/settings/prod-sync': 'System:READ',
  '/settings/ai': 'System:READ',
  '/settings/bitrix-bot': 'System:READ',
  '/settings/mail': 'System:READ',
  '/settings/marketplace-link': 'System:READ',
  '/settings/legacy-photogen': 'System:READ',
  '/settings/backlog': 'Backlog:READ',
  // «Мои обращения» автора: своё право READ_OWN (есть у всех ролей, включая дизайнера), а не беклог целиком.
  '/backlog/mine': 'Backlog:READ_OWN',
  '/stocks/stop-list': 'Stocks:READ',
  '/stocks/autoupdate': 'Stocks:READ',
  '/stocks/runs': 'Stocks:READ',
  '/stocks/warehouses': 'Stocks:READ',
  '/stocks/settings': 'Stocks:UPDATE',
  '/stocks/banned-brands': 'Stocks:READ',
  '/stocks/unliquid': 'Stocks:READ',
  '/stocks/manual': 'Stocks:READ',
  '/oauth/consent': 'Agent:CREATE',
  '/settings/agent': 'Agent:READ',
  '/settings/agents': 'Agent:READ',
    '/settings/business-access': ANY,
    '/settings/business-agents': ANY,
  '/settings': ANY,
  '/notifications': ANY,
  '/settings/notifications': ANY,
  '/design-system': ANY,
}

const ROUTE_ROLE_GATES: ReadonlyArray<{
  prefix: string
  roles: readonly string[]
}> = [
  { prefix: '/layout-generator', roles: ['admin', 'owner'] },
  { prefix: '/layout-generator-v2', roles: ['admin', 'owner'] },
  { prefix: '/asset-library', roles: ['admin', 'owner'] },
  { prefix: '/oauth/consent', roles: ['admin', 'owner'] },
  { prefix: '/settings/agent', roles: ['admin', 'owner'] },
  { prefix: '/settings/agents', roles: ['admin', 'owner'] },
    { prefix: '/settings/business-access', roles: ['admin', 'owner'] },
]

function normalizePath(pathname: string): string {
  const clean = pathname.split(/[?#]/, 1)[0] || '/'
  if (clean === '/') return clean
  return clean.replace(/\/+$/, '') || '/'
}

/** Точное совпадение, затем самое длинное явно объявленное `/*`-поддерево. */
export function accessFor(pathname: string): RouteAccess | undefined {
  const normalized = normalizePath(pathname)
  const exact = ROUTE_ACCESS[normalized]
  if (exact) return exact

  let match: { length: number; access: RouteAccess } | undefined
  for (const [pattern, access] of Object.entries(ROUTE_ACCESS)) {
    if (!pattern.endsWith('/*')) continue
    const prefix = pattern.slice(0, -2)
    if (normalized !== prefix && !normalized.startsWith(`${prefix}/`)) continue
    if (!match || prefix.length > match.length) match = { length: prefix.length, access }
  }
  return match?.access
}

export function hasPermission(
  access: RouteAccess,
  permissions: readonly string[],
): boolean {
  return access === ANY || permissions.includes(access)
}

export function canAccessPath(pathname: string, permissions: readonly string[]): boolean {
  const access = accessFor(pathname)
  return access !== undefined && hasPermission(access, permissions)
}

/** Роли, которым открыт путь сверх RBAC-права; `null` — ролевой границы нет. */
export function rolesForPath(pathname: string): readonly string[] | null {
  const normalized = normalizePath(pathname)
  const gate = ROUTE_ROLE_GATES.find(({ prefix }) => (
    normalized === prefix || normalized.startsWith(`${prefix}/`)
  ))
  return gate ? gate.roles : null
}

/** Дополнительная beta-граница, не зависящая от случайно выданного RBAC-права. */
export function isRoleAllowedForPath(pathname: string, role: string | null | undefined): boolean {
  const roles = rolesForPath(pathname)
  return !roles || roles.includes(String(role || '').toLowerCase())
}

export function homePathFor(permissions: readonly string[]): string {
  if (permissions.includes('Product:READ')) return '/'
  if (permissions.includes('DesignerCabinet:READ')) return '/designer/create'
  // Аналитика продаж открывается со «Сводки»: порядок ROUTE_ACCESS привёл бы на первую попавшуюся страницу
  // с тем же правом («Аналитика дизайнов»).
  if (permissions.includes('SalesAnalytics:READ')) return '/analytics'

  for (const [path, access] of Object.entries(ROUTE_ACCESS)) {
    if (path.includes('*') || access === ANY) continue
    if (permissions.includes(access)) return path
  }
  return '/settings'
}
